import { faCheck, faCheckCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormError } from '@kcftech/design-system';
import { UserConfirmPasswordRequest } from '@kcftech/prometheustypescriptapiclient';
import { DefaultButton } from 'components/button/DefaultButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { PasswordInput } from 'components/forms/PasswordInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { sdClient } from 'contexts/SDKClientContext';
import { Heading } from 'pages/Login/Login';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { useQueryParams } from 'utils/useQueryParams';

const RequirementsList = styled.ul`
    list-style: disc;
    padding-left: 4rem;
`;

const Requirement = styled.li``;

const IconWrapper = styled.div`
    margin-left: -4rem; /* should be same as requirements list padding */
    margin-right: 2.25rem;
    display: inline-block;
    width: 1.6rem;
    text-align: center;
`;

const ValidityIcon = styled(FontAwesomeIcon)`
    font-size: 1.6rem;
`;

const Validity = ({
    valid = false,
    visible = true,
}: {
    valid: boolean;
    visible: boolean;
}) => (
    <>
        {visible && (
            <IconWrapper>
                <ValidityIcon
                    icon={valid ? faCheck : faTimes}
                    color={valid ? tokens.icon.positive : tokens.error.default}
                    aria-label={valid ? 'Valid' : 'Invalid'}
                />
            </IconWrapper>
        )}
    </>
);

const ConfirmationCheckIcon = styled(FontAwesomeIcon)`
    font-size: 8rem;
    color: ${tokens.action.confirm};
`;

type ResetPasswordForm = {
    newPassword: string;
    confirmPassword: string;
};

type Validation = {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    specialCharacter: boolean;
};

type UsePasswordResetToken = {
    /**
     * The query parameter that should contain the actual password reset token.
     */
    tokenParameter?: string;
    /**
     * The query parameter that should contain a timestamp for when the token was generated.
     */
    tokenDateParameter?: string;
    /**
     * How long the token is valid for (in minutes).
     */
    expiresAfterMinutes?: number;

    /** The User's email to reset the password */
    userEmail?: string;
};
function usePasswordResetToken(props?: UsePasswordResetToken) {
    const queryParams = useQueryParams();

    const token = queryParams.get(props?.tokenParameter ?? 'resetCode');
    const tokenDate = queryParams.get(props?.tokenDateParameter ?? 'requestedAt');
    const email = queryParams.get(props?.userEmail ?? 'email');
    const expiresAfterMinutes = props?.expiresAfterMinutes ?? 60 * 24;

    // If there is no token, or no token date parameter or no email, act is if there is no token.
    if (!token || !tokenDate || !email) {
        return { token: null };
    }

    try {
        const expirationDate = new Date(tokenDate);
        expirationDate.setMinutes(expirationDate.getMinutes() + expiresAfterMinutes);
        const isValid = expirationDate > new Date();
        return { token, email, isValid };
    } catch (e) {
        // If any errors occur, act as if there is no token and log the error.
        // eslint-disable-next-line no-console
        console.error(e);
        return { token: null };
    }
}

export function ResetPassword() {
    const { token, isValid, email } = usePasswordResetToken();
    const [wasDirty, setWasDirty] = useState(false);
    const [didResetPassword, setDidResetPassword] = useState(false);
    const [validation, setValidation] = useState<Validation>({
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        specialCharacter: false,
    });
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [isNewPasswordValid, setIsNewPasswordValid] = useState<boolean>(false);
    const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const {
        register,
        handleSubmit,
        getValues,
        trigger,
        formState: { errors, isDirty, isSubmitting },
    } = useForm<ResetPasswordForm, Validation>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            newPassword: '',
            confirmPassword: '',
        },
    });

    useEffect(() => {
        if (isDirty) {
            setWasDirty(true);
        }
    }, [isDirty]);

    useEffect(() => {
        setIsFormValid(isNewPasswordValid && isConfirmPasswordValid);
    }, [isConfirmPasswordValid, isNewPasswordValid]);

    const onReset = handleSubmit(async (data: ResetPasswordForm) => {
        setErrorMessage(undefined);
        if (!email || !token) {
            setDidResetPassword(false);
        } else {
            await sdClient
                .confirmPasswordSD4({
                    body: new UserConfirmPasswordRequest({
                        Email: email,
                        ResetCode: token,
                        NewPassword: data.newPassword,
                    }),
                })
                .then(() => setDidResetPassword(true))
                .catch((result) => {
                    setErrorMessage(result.detail);
                });
        }
    });

    if (didResetPassword) {
        return (
            <>
                <DocumentTitle title="Reset Password" />
                <div className="flex">
                    <Heading className="flex-grow-1">
                        Your password has been reset!
                    </Heading>
                    <ConfirmationCheckIcon icon={faCheckCircle} />
                </div>
            </>
        );
    }

    if (!token || !isValid) {
        return (
            <>
                <DocumentTitle title="Reset Password" />
                <div className="flex">
                    <Heading className="flex-grow-1">
                        Your password reset link has expired.
                    </Heading>
                </div>
                <p>To reset your password, please request another password reset.</p>
            </>
        );
    }

    const validateNewPassword = (newPassword: string) => {
        const requirements = {
            length: newPassword.length >= 8,
            uppercase: newPassword.toLowerCase() !== newPassword,
            lowercase: newPassword.toUpperCase() !== newPassword,
            number: /[0-9]/.test(newPassword),
            specialCharacter: /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`]/.test(newPassword),
        };
        setValidation(requirements);

        const isPasswordValid =
            requirements.length &&
            requirements.uppercase &&
            requirements.lowercase &&
            requirements.number &&
            requirements.specialCharacter;

        setIsNewPasswordValid(isPasswordValid);

        // Revalidate confirm password if password changes
        if (getValues().confirmPassword !== '') {
            trigger('confirmPassword');
        }

        if (!isPasswordValid) {
            return 'Password requirements not met. Please enter a valid password.';
        }
        return undefined;
    };

    const validateConfirmPassword = (confirmPassword: string) => {
        const isPasswordMatch = getValues().newPassword === confirmPassword;

        setIsConfirmPasswordValid(isPasswordMatch);

        if (!isPasswordMatch) {
            return 'Passwords must match. Please enter matching passwords.';
        }
        return undefined;
    };

    return (
        <>
            <DocumentTitle title="Reset Password" />
            <form onSubmit={onReset}>
                <Heading>Reset your password</Heading>
                <p>In order to protect your account, passwords must contain:</p>
                <RequirementsList>
                    <Requirement aria-label="8 characters">
                        <Validity valid={validation.length} visible={wasDirty} />8
                        characters
                    </Requirement>
                    <Requirement aria-label="an uppercase letter">
                        <Validity valid={validation.uppercase} visible={wasDirty} />
                        an uppercase letter
                    </Requirement>
                    <Requirement aria-label="a lowercase letter">
                        <Validity valid={validation.lowercase} visible={wasDirty} />a
                        lowercase letter
                    </Requirement>
                    <Requirement aria-label="a number">
                        <Validity valid={validation.number} visible={wasDirty} />a number
                    </Requirement>
                    <Requirement aria-label="a special character">
                        <Validity
                            valid={validation.specialCharacter}
                            visible={wasDirty}
                        />
                        a special character
                    </Requirement>
                </RequirementsList>
                <ValidatableLabel
                    label="New Password"
                    required
                    className="mb-20"
                    error={errors.newPassword}
                >
                    <PasswordInput
                        autofocus
                        placeholder="Enter a new password"
                        className="my-5"
                        defaultVisible
                        invalid={errors.newPassword !== undefined}
                        showValidState={getValues().newPassword !== ''}
                        {...register('newPassword', {
                            validate: validateNewPassword,
                        })}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Confirm New Password"
                    required
                    className="mb-20"
                    error={errors.confirmPassword}
                >
                    <PasswordInput
                        placeholder="Confirm new password"
                        className="my-5"
                        defaultVisible
                        invalid={errors.confirmPassword !== undefined}
                        showValidState={getValues().confirmPassword !== ''}
                        {...register('confirmPassword', {
                            validate: validateConfirmPassword,
                        })}
                    />
                </ValidatableLabel>
                <FormError message={errorMessage} />
                <footer>
                    <DefaultButton
                        type="button"
                        onClick={onReset}
                        disabled={!isFormValid || isSubmitting}
                    >
                        Reset
                    </DefaultButton>
                </footer>
            </form>
        </>
    );
}
